import React, { useEffect, useState } from "react";
import BaseModal from "Components/Base/BaseModal";
import BaseInput from "Components/Base/BaseInput";
import { BaseSelect } from "Components/Base/BaseSelect";
import { NormalDate } from "Components/constants/Common";
import {
  DESTINATION,
  LR_COPY,
  LR_DATE,
  LR_NO,
  MODE_OF_TRANSPORTATION,
  ModeOfTransportType,
  ORIGIN,
  VEHICLE_NO,
} from "Components/constants/Order";
import { useFormik } from "formik";
import { cancelLabel, submitLabel } from "Components/constants/Role";
import { addTransportDetails, viewTransportDetails } from "Api/Order";
import { Link, useNavigate, useParams } from "react-router-dom";
import { checkStatusCodeSuccess, Loading } from "Components/constants/Common";
import { toast } from "react-toastify";
import {
  fileLimitErrorMessage,
  fileTypePDFErrorMessage,
  fileTypePDFJPGPNGErrorMessage,
  InputPlaceHolder,
  SelectPlaceHolder,
} from "Components/constants/validation";
import { fileUpload } from "Api/common";
import { BaseImageURL } from "Api/services";
import { ViewFileLabel } from "Components/constants/Staff";
import { Spinner } from "reactstrap";

interface TransportModalProps {
  isTransportModalOpen: boolean;
  toggleTransportModal: () => void;
  orderId: number;
  setIsTransportModalOpen: React.Dispatch<React.SetStateAction<any>>;
}

interface TransportData {
  id?: number;
  order_id?: number;
  mode_of_transaction?: string;
  origin?: string;
  destination?: string;
  vehicle_no?: string;
  lr_no?: string;
  lr_copy?: string;
  lr_date?: string;
}

const TransportModal: React.FC<TransportModalProps> = ({
  isTransportModalOpen,
  toggleTransportModal,
  setIsTransportModalOpen,
  orderId,
}) => {
  const [btnLoader, setBtnLoader] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [transportId, setTransportId] = useState<number | null>(null);
  const [transportData, setTransportData] = useState<TransportData>();
  const navigate = useNavigate();

  let date: any;
  if (transportData) {
    date = transportData.lr_date;
  }
  let formattedDate = NormalDate(date);

  const handleFileUpload = (file: any, name: string) => {
    setLoader(true);
    const payload = new FormData();
    payload.append("files", file);
    fileUpload(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.StatusCodes)) {
          formik.setFieldValue(name, String(res?.data[0]));
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const handleFileChange = (event: any, name: string) => {
    const fileInput = event.target;
    const file = fileInput?.files[0];
    const maxSizeInBytes = 1024 * 1024;

    if (file && file.size > maxSizeInBytes) {
      toast.error(fileLimitErrorMessage);
      fileInput.value = "";
      return;
    }

    if (
      file &&
      file.type !== "application/pdf" &&
      file.type !== "image/jpeg" &&
      file.type !== "image/png"
    ) {
      toast.error(fileTypePDFJPGPNGErrorMessage);
      fileInput.value = "";
      return;
    }

    if (file) {
      handleFileUpload(file, name);
    }
  };

  const viewTransportData = (orderId: number) => {
    setLoader(true);
    viewTransportDetails(orderId)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setTransportId(res?.data?.id);
          setTransportData(res?.data);
         
        } else {
          setTransportData({});
          setTransportId(null);
        }
      })
      .catch((err) => {
        setTransportData({});
        setTransportId(null);
        return err;
      })
      .finally(() => {
        setLoader(false);
        setBtnLoader(false);
      });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      modeOfTransportation: transportData?.mode_of_transaction || "",
      origin: transportData?.origin || "",
      destination: transportData?.destination || "",
      vehicleNo: transportData?.vehicle_no || "",
      lrNo: transportData?.lr_no || "",
      lrCopy: transportData?.lr_copy || "",
      lrDate: transportData ? formattedDate : "",
    },
    onSubmit: (values) => {
      setBtnLoader(true);
      const payload = {
        mode_of_transaction: values.modeOfTransportation || null,
        origin: values.origin,
        destination: values.destination,
        vehicle_no: values.vehicleNo,
        lr_no: values.lrNo,
        lr_copy: values.lrCopy,
        lr_date: values.lrDate,
      };

      addTransportDetails(Number(orderId), payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            setIsTransportModalOpen(false);
            navigate("/order");
          } else {
            toast.error(res?.message);
          }
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || err?.message);
          return err;
        })
        .finally(() => {
          setBtnLoader(false);
        });
    },
  });

  useEffect(() => {
    if (isTransportModalOpen) {
      viewTransportData(orderId);
    } else {
      // Reset form values when modal is closed without submission
      formik.resetForm();
    }
  }, [orderId, isTransportModalOpen]);

  return (
    <BaseModal
      open={isTransportModalOpen}
      toggle={toggleTransportModal}
      title="Transport Information"
      submit={formik.handleSubmit}
      closeLabel={cancelLabel}
      submitLabel={submitLabel}
      closeLabelColor="danger"
      submitLabelColor="success"
      loader={btnLoader}
      isSubmitDisabled={!formik.dirty}
    >
      {loader ? (
        <div className="d-flex justify-content-center align-items-center">
          <Spinner>{Loading}</Spinner>
        </div>
      ) : (
        <div className="form">
          <div className="mb-2">
            <BaseSelect
              name="modeOfTransportation"
              label={MODE_OF_TRANSPORTATION}
              options={ModeOfTransportType}
              value={formik.values.modeOfTransportation}
              placeholder={SelectPlaceHolder(MODE_OF_TRANSPORTATION)}
              handleChange={(field: any, value: any) => {
                formik.setFieldValue(field, value);
              }}
              handleBlur={formik.handleBlur}
              error={formik.errors.modeOfTransportation}
              touched={formik.touched.modeOfTransportation}
            />
          </div>
          <div className="mb-2">
            <BaseInput
              label={ORIGIN}
              type="text"
              className="form-control"
              name="origin"
              placeholder={InputPlaceHolder(ORIGIN)}
              value={formik.values.origin}
              onChange={formik.handleChange}
            />
          </div>
          <div className="mb-2">
            <BaseInput
              label={DESTINATION}
              type="text"
              className="form-control"
              name="destination"
              placeholder={InputPlaceHolder(DESTINATION)}
              value={formik.values.destination}
              onChange={formik.handleChange}
            />
          </div>
          <div className="mb-2">
            <BaseInput
              label={VEHICLE_NO}
              type="text"
              className="form-control"
              name="vehicleNo"
              placeholder={InputPlaceHolder(VEHICLE_NO)}
              value={formik.values.vehicleNo}
              onChange={formik.handleChange}
            />
          </div>
          <div className="mb-2">
            <BaseInput
              label={LR_NO}
              type="text"
              className="form-control"
              name="lrNo"
              placeholder={InputPlaceHolder(LR_NO)}
              value={formik.values.lrNo}
              onChange={formik.handleChange}
            />
          </div>
          <div className="mb-2">
            <BaseInput
              name="lrCopy"
              label={LR_COPY}
              type="file"
              tooltip={true}
              tooltipIcon="help-circle-outline"
              tooltipIconColor="info"
              accept="application/pdf, image/jpeg, image/png"  
              tooltipText="Only .pdf, .jpg, and .png file types are allowed"
              onChange={(e) => handleFileChange(e, "lrCopy")}
              handleBlur={formik.handleBlur}
              error={formik.errors.lrCopy}
              touched={formik.touched.lrCopy}
            />
            <div>
              {formik.values.lrCopy && (
                <span className="fw-normal">
                  <Link
                    target="_blank"
                    className="text-link font-weight-bold"
                    to={`${BaseImageURL}${formik.values.lrCopy}`}
                  >
                    {ViewFileLabel}
                  </Link>
                </span>
              )}
            </div>
          </div>
          <div className="mb-2">
            <BaseInput
              name="lrDate"
              label={LR_DATE}
              type="date"
              placeholder={InputPlaceHolder(LR_DATE)}
              onChange={formik.handleChange}
              value={formik.values.lrDate}
            />
          </div>
        </div>
      )}
    </BaseModal>
  );
};

export default TransportModal;
